import { useEffect, useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip,
    CircularProgress, Divider, Drawer, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import { Close, ExpandMore, WarningAmber } from '@mui/icons-material';
import { useIFood } from '../../../marketplace/hooks/useIFood';
import { IMarketplace } from '../../../marketplace/interfaces/IMarketplace';
import { TipoMarketplace, TipoMarketplaceTitulo } from '../../../marketplace/enum/TipoMarketplace';
import {
    ICategoriaPreviaImportacaoIFood, IItemPreviaImportacaoIFood, IResultadoImportacaoCardapioIFood,
    SituacaoItemImportacaoIFood, TipoItemImportacaoIFood
} from '../../../marketplace/interfaces/IImportacaoCardapioIFood';

interface ImportarCardapioIFoodDrawerProps {
    open: boolean;
    marketplaceUuid: string;
    marketplaces: IMarketplace[];
    handleCancelar: () => void;
    handleRefresh: () => void;
}

const ROTULO_TIPO: Record<TipoItemImportacaoIFood, string> = {
    pizza: 'Pizza',
    preparado: 'Preparado',
    industrializado: 'Industrializado'
};

const ROTULO_SITUACAO: Record<Exclude<SituacaoItemImportacaoIFood, 'novo'>, string> = {
    existente: 'Já vinculado',
    indisponivel: 'Indisponível no iFood',
    ignorado: 'Não importável'
};

const formatarValor = (valor?: number) =>
    valor === undefined || valor === null ? '' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// A mesma categoria do iFood pode gerar duas prévias (itens comuns + pizza); a seleção é pelo id do iFood.
const chavePrevia = (categoria: ICategoriaPreviaImportacaoIFood) => `${categoria.codigoExterno}::${categoria.modelo}`;
const contarNovos = (categoria: ICategoriaPreviaImportacaoIFood) => categoria.itens.filter(item => item.situacao === 'novo').length;

const LinhaItemPrevia = ({ item }: { item: IItemPreviaImportacaoIFood }) => {
    const importavel = item.situacao === 'novo';
    const detalhe = item.tipo === 'pizza'
        ? `${item.sabores ?? 0} ${item.sabores === 1 ? 'sabor' : 'sabores'}`
        : item.complementos ? `${item.complementos} ${item.complementos === 1 ? 'complemento' : 'complementos'}` : undefined;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, opacity: importavel ? 1 : 0.55 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.nome}</Typography>
                    <Chip size="small" variant="outlined" label={ROTULO_TIPO[item.tipo] ?? item.tipo} />
                    {!importavel && (
                        <Chip size="small" label={ROTULO_SITUACAO[item.situacao as Exclude<SituacaoItemImportacaoIFood, 'novo'>]} />
                    )}
                    {item.avisos?.length > 0 && (
                        <Tooltip title={<>{item.avisos.map(aviso => <div key={aviso}>{aviso}</div>)}</>}>
                            <WarningAmber fontSize="small" color="warning" />
                        </Tooltip>
                    )}
                </Box>
                {detalhe && <Typography variant="caption" color="text.secondary">{detalhe}</Typography>}
            </Box>
            {item.tipo !== 'pizza' && (
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatarValor(item.preco)}</Typography>
            )}
        </Box>
    );
};

export const ImportarCardapioIFoodDrawer = ({ open, marketplaceUuid, marketplaces, handleCancelar, handleRefresh }: ImportarCardapioIFoodDrawerProps) => {
    const { previsualizarImportacaoCardapio, importarCardapio, loading } = useIFood();
    const [categorias, setCategorias] = useState<ICategoriaPreviaImportacaoIFood[] | null>(null);
    const [selecionadas, setSelecionadas] = useState<string[]>([]);
    const [importarImagens, setImportarImagens] = useState(true);
    const [importarIndisponiveis, setImportarIndisponiveis] = useState(false);
    const [resultado, setResultado] = useState<IResultadoImportacaoCardapioIFood | null>(null);

    // Categorias novas ficam visíveis nos canais da própria Space Food; nunca no iFood.
    const opcoesMarketplaces = useMemo(
        () => marketplaces.filter(marketplace => marketplace.tipo !== TipoMarketplace.IFOOD),
        [marketplaces]
    );
    const [marketplacesSelecionados, setMarketplacesSelecionados] = useState<IMarketplace[]>([]);

    useEffect(() => {
        if (open) setMarketplacesSelecionados(opcoesMarketplaces);
    }, [open, opcoesMarketplaces]);

    useEffect(() => {
        if (!open) return;
        let ativo = true;

        (async () => {
            setCategorias(null);
            const previa = await previsualizarImportacaoCardapio(marketplaceUuid, importarIndisponiveis);
            if (!ativo) return;

            const lista = previa?.categorias ?? [];
            setCategorias(lista);
            setSelecionadas(Array.from(new Set(lista.filter(categoria => contarNovos(categoria) > 0).map(categoria => categoria.codigoExterno))));
        })();

        return () => { ativo = false; };
    }, [open, marketplaceUuid, importarIndisponiveis]);

    const totalNovos = useMemo(() => (categorias ?? [])
        .filter(categoria => selecionadas.includes(categoria.codigoExterno))
        .reduce((total, categoria) => total + contarNovos(categoria), 0), [categorias, selecionadas]);

    const totalAvisos = useMemo(() => (categorias ?? [])
        .filter(categoria => selecionadas.includes(categoria.codigoExterno))
        .flatMap(categoria => categoria.itens)
        .filter(item => item.situacao === 'novo' && item.avisos?.length).length, [categorias, selecionadas]);

    const alternarCategoria = (codigoExterno: string) => {
        setSelecionadas(atuais => atuais.includes(codigoExterno)
            ? atuais.filter(codigo => codigo !== codigoExterno)
            : [...atuais, codigoExterno]);
    };

    const fechar = () => {
        if (loading && categorias) return;
        const houveImportacao = !!resultado;
        setResultado(null);
        setCategorias(null);
        setImportarIndisponiveis(false);
        handleCancelar();
        if (houveImportacao) handleRefresh();
    };

    const importar = async () => {
        const resposta = await importarCardapio(marketplaceUuid, {
            categoriasIds: selecionadas,
            importarImagens,
            importarIndisponiveis,
            marketplacesCategoria: marketplacesSelecionados.map(marketplace => ({
                tipo: marketplace.tipo,
                uuid: marketplace.tipo !== TipoMarketplace.PDV ? marketplace.uuid : undefined
            }))
        });

        if (resposta) setResultado(resposta);
    };

    const importando = loading && !!categorias;

    return (
        <Drawer anchor="right" open={open} onClose={fechar} sx={{ '& .MuiDrawer-paper': { width: 'min(800px, 100vw)', padding: '1rem', boxSizing: 'border-box' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Importar cardápio do iFood</Typography>
                <IconButton onClick={fechar} disabled={importando}><Close /></IconButton>
            </Box>

            {resultado ? (
                <Box sx={{ py: 2 }}>
                    <Alert severity={resultado.erros.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        {resultado.produtosImportados} {resultado.produtosImportados === 1 ? 'produto importado' : 'produtos importados'} em{' '}
                        {resultado.categoriasCriadas} {resultado.categoriasCriadas === 1 ? 'categoria nova' : 'categorias novas'}
                        {resultado.categoriasAtualizadas > 0 && ` e ${resultado.categoriasAtualizadas} existente${resultado.categoriasAtualizadas === 1 ? '' : 's'}`}.
                    </Alert>
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                        <Typography variant="body2">Vínculos com o iFood criados: <b>{resultado.vinculosCriados}</b></Typography>
                        <Typography variant="body2">
                            Imagens importadas: <b>{resultado.imagensImportadas}</b>
                            {resultado.imagensComFalha > 0 && ` (${resultado.imagensComFalha} não puderam ser baixadas)`}
                        </Typography>
                    </Stack>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Os pedidos do iFood passam a usar os produtos importados em KDS, estoque, comanda e financeiro.
                        Revise tributação (NCM/CFOP), ficha técnica e configurações de KDS dos produtos importados.
                    </Alert>
                    {resultado.erros.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Itens não importados</Typography>
                            {resultado.erros.map((erro, index) => (
                                <Typography key={index} variant="body2" color="text.secondary">
                                    <b>{erro.categoria}{erro.produto ? ` / ${erro.produto}` : ''}:</b> {erro.mensagem}
                                </Typography>
                            ))}
                        </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button variant="contained" onClick={fechar}>Concluir</Button>
                    </Box>
                </Box>
            ) : !categorias ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">Lendo o cardápio do iFood...</Typography>
                </Box>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Os itens do iFood viram produtos da Space Food: pizzas geram um produto por tamanho (com sabores,
                        massas e bordas), itens com complementos viram preparados e itens com código de barras (EAN) viram
                        industrializados. Cada produto já nasce vinculado ao iFood. Itens já vinculados não são duplicados.
                    </Typography>

                    <Stack spacing={1} sx={{ my: 2 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={opcoesMarketplaces}
                            value={marketplacesSelecionados}
                            disableCloseOnSelect
                            getOptionLabel={(marketplace) => `${marketplace.nome ?? '-'} - ${TipoMarketplaceTitulo[marketplace.tipo] ?? marketplace.tipo}`}
                            isOptionEqualToValue={(opcao, valor) => opcao.uuid === valor.uuid && opcao.tipo === valor.tipo}
                            onChange={(_evento, valores) => setMarketplacesSelecionados(valores)}
                            renderInput={(params) => (
                                <TextField {...params} label="Exibir as categorias novas em" helperText="As categorias continuam editáveis depois da importação" />
                            )}
                        />
                        <FormControlLabel
                            control={<Switch checked={importarImagens} onChange={(_evento, marcado) => setImportarImagens(marcado)} />}
                            label="Importar imagens dos produtos"
                        />
                        <FormControlLabel
                            control={<Switch checked={importarIndisponiveis} disabled={loading} onChange={(_evento, marcado) => setImportarIndisponiveis(marcado)} />}
                            label="Incluir itens pausados no iFood (entram inativos)"
                        />
                    </Stack>

                    <Divider />

                    <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
                        {categorias.length === 0 && (
                            <Alert severity="info">Nenhuma categoria encontrada no cardápio do iFood.</Alert>
                        )}
                        {categorias.map(categoria => {
                            const novos = contarNovos(categoria);
                            const marcada = selecionadas.includes(categoria.codigoExterno);

                            return (
                                <Accordion
                                    key={chavePrevia(categoria)}
                                    disableGutters
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '8px', boxShadow: 'none', '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, pr: 1, minWidth: 0 }}>
                                            <Checkbox
                                                size="small"
                                                checked={marcada}
                                                disabled={novos === 0 || importando}
                                                onClick={(evento) => evento.stopPropagation()}
                                                onChange={() => alternarCategoria(categoria.codigoExterno)}
                                            />
                                            <Typography variant="subtitle2" noWrap>{categoria.descricao}</Typography>
                                            {categoria.modelo === 'pizza' && <Chip size="small" variant="outlined" label="Pizza" />}
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                {novos === 0 ? 'Nada novo' : `${novos} ${novos === 1 ? 'novo' : 'novos'}`} / {categoria.itens.length}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        {categoria.itens.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">Nenhum item nesta categoria.</Typography>
                                        ) : categoria.itens.map((item, index) => (
                                            <Box key={`${item.codigoExterno}-${index}`}>
                                                {index > 0 && <Divider />}
                                                <LinhaItemPrevia item={item} />
                                            </Box>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>

                    {totalAvisos > 0 && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {totalAvisos} {totalAvisos === 1 ? 'item tem' : 'itens têm'} observações (ícone de alerta). Revise esses produtos depois da importação.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            {totalNovos} {totalNovos === 1 ? 'produto será importado' : 'produtos serão importados'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" disabled={importando} onClick={fechar}>Cancelar</Button>
                            <Button
                                variant="contained"
                                disabled={importando || totalNovos === 0}
                                onClick={importar}
                                startIcon={importando ? <CircularProgress size={16} color="inherit" /> : undefined}
                            >
                                {importando ? 'Importando...' : 'Importar'}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Drawer>
    );
};
